import React, { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

type Q = { id: string }
type SessionResp = {
  ok: true; exam: string; sessionId: string; questions: Q[];
  extension?: string; timeLimitSeconds?: number;
  discordId?: string; candidateTag?: string | null; examinerId?: string | null;
}
type SubmitResp = { ok: boolean; summaryLink?: string; error?: string }

export default function ExamPage() {
  const { token: legacyToken = '' } = useParams()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || legacyToken
  const tokenQuery = `?token=${encodeURIComponent(token)}`
  const [meta, setMeta] = useState<SessionResp | null>(null)
  const [idx, setIdx] = useState(0)
  const [answers, setAnswers] = useState<{ id: string; answer: string; timeMs: number; pasted?: boolean }[]>([])
  const [value, setValue] = useState('')
  const [pasted, setPasted] = useState(false)
  const [err, setErr] = useState('')
  const [finishing, setFinishing] = useState(false)
  const [finished, setFinished] = useState(false)
  const [started, setStarted] = useState(false)
  const [startedAt, setStartedAt] = useState<string | null>(null)
  const [terminationReason, setTerminationReason] = useState('')

  const timeLimit = (meta?.timeLimitSeconds ?? 15) * 1000
  const qStartRef = useRef<number>(0)
  const [leftMs, setLeftMs] = useState(timeLimit)
  const timerRef = useRef<number | null>(null)

  const answersRef = useRef(answers)
  const leaveTimerRef = useRef<number | null>(null)
  const strikesRef = useRef(0)

  useEffect(() => { answersRef.current = answers }, [answers])
  useEffect(() => () => stopTimer(), [])

  // cursor (FULL PAGE)
  const lastSentRef = useRef(0)

  useEffect(() => {
    (async () => {
      const r = await fetch(`/api/session${tokenQuery}`)
      if (!r.ok) throw new Error('Sesja wygasła lub nie istnieje')
      const d: SessionResp = await r.json()
      setMeta(d)
      setLeftMs((d.timeLimitSeconds ?? 15) * 1000)
    })().catch(e => setErr(String(e)))
  }, [token])

  useEffect(() => {
    if (!meta || !started || finished) return
    startTimer(meta.timeLimitSeconds ?? 15)
    setValue(''); setPasted(false)
    fetch(`/api/live${tokenQuery}`, { method:'POST', headers:{'Content-Type':'application/json'},
      body: JSON.stringify({ type:'question', index: idx }) })
  }, [idx, meta, token, started, finished])

  useEffect(() => {
    if (!meta) return
    const next = meta.questions[idx + 1]
    if (!next) return
    const preload = new Image()
    preload.src = `/assets/${meta.exam}/${next.id}.${meta.extension || 'jpg'}`
  }, [idx, meta])

  // ENTER → next/finish
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        if (!meta || !started || finished || terminationReason) return
        if (idx < meta.questions.length - 1) handleNext(false)
        else handleFinish(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [idx, meta, value, pasted, started, finished, terminationReason])

  // FULL-PAGE CURSOR CAPTURE
  useEffect(() => {
    if (!started || finished) return
    const onMove = (e: MouseEvent) => {
      const now = performance.now()
      if (now - lastSentRef.current < 50) return
      lastSentRef.current = now
      const x = e.clientX / window.innerWidth
      const y = e.clientY / window.innerHeight
      fetch(`/api/live${tokenQuery}`, {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ type:'cursor', index: idx, x, y })
      })
    }
    window.addEventListener('mousemove', onMove)
    return () => window.removeEventListener('mousemove', onMove)
  }, [idx, token, started, finished])

    useEffect(() => {
      if (!started || finished || terminationReason) return
      const reasonText = 'Przerwano egzamin z powodu podejrzenia oszustwa.'

      const cancelCountdown = () => {
        if (leaveTimerRef.current) { window.clearTimeout(leaveTimerRef.current); leaveTimerRef.current = null }
      }

      const handleLeave = () => {
        const strike = strikesRef.current + 1
        strikesRef.current = strike
        fetch(`/api/live${tokenQuery}`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ type:'strike', strike }) })

        if (strike >= 2) {
          terminateExam(reasonText)
          return
        }

        cancelCountdown()
        leaveTimerRef.current = window.setTimeout(() => terminateExam(reasonText), 5000) as unknown as number
      }

      const onMouseLeave = (e: MouseEvent) => { if (!e.relatedTarget) handleLeave() }
      const onBlur = () => handleLeave()
      const onReturn = () => cancelCountdown()

      document.addEventListener('mouseleave', onMouseLeave)
      window.addEventListener('blur', onBlur)
      document.addEventListener('mouseenter', onReturn)
      window.addEventListener('focus', onReturn)

      return () => {
        cancelCountdown()
        document.removeEventListener('mouseleave', onMouseLeave)
        window.removeEventListener('blur', onBlur)
        document.removeEventListener('mouseenter', onReturn)
        window.removeEventListener('focus', onReturn)
      }
    }, [started, finished, terminationReason, token])

  function startTimer(seconds: number) {
    if (!started || finished || terminationReason) return
    stopTimer()
    const ms = seconds * 1000
    setLeftMs(ms)
    qStartRef.current = performance.now()
    timerRef.current = window.setInterval(() => {
      setLeftMs(prev => {
        const n = prev - 100
        if (n <= 0) { stopTimer(); handleNext(true); return 0 }
        return n
      })
    }, 100) as unknown as number
  }
  function stopTimer() { if (timerRef.current) { window.clearInterval(timerRef.current); timerRef.current = null } }

  function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (!started || finished || terminationReason) return
    const v = e.target.value
    setValue(v)
    fetch(`/api/live${tokenQuery}`, { method:'POST', headers:{'Content-Type':'application/json'},
      body: JSON.stringify({ type:'typing', index: idx, value: v.slice(0, 80) }) })
  }
  function onPaste() {
    if (!started || finished || terminationReason) return
    setPasted(true)
    fetch(`/api/live${tokenQuery}`, { method:'POST', headers:{'Content-Type':'application/json'},
      body: JSON.stringify({ type:'paste', index: idx }) })
  }

  function collect(allowEmpty = false) {
    if (!meta) return null
    const q = meta.questions[idx]; if (!q) return null
    const elapsed = Math.max(0, performance.now() - qStartRef.current)
    const raw = value.trim()
    const finalAnswer = raw.length === 0 ? 'BRAK WPISU' : raw
    if (!allowEmpty && raw.length === 0) { setErr('Wpisz odpowiedź lub poczekaj na auto-przejście.'); return null }
    return { id: q.id, answer: finalAnswer, timeMs: Math.round(elapsed), pasted }
  }

  function handleNext(auto:boolean) {
    if (!started || finishing || finished || terminationReason) return
    setErr(''); stopTimer()
    const rec = collect(true)
    let nextAnswers = answersRef.current
    if (rec) {
      nextAnswers = [...answersRef.current, rec]
      setAnswers(nextAnswers)
      answersRef.current = nextAnswers
      fetch(`/api/live${tokenQuery}`, { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ type:'answered', index: idx, ...rec }) })
    }
    if (!meta) return
    if (idx + 1 < meta.questions.length) setIdx(i => i + 1)
    else handleFinish(true, nextAnswers)
  }

  async function handleFinish(fromNext:boolean, finalAnswersOverride?: { id: string; answer: string; timeMs: number; pasted?: boolean }[], reason?: string) {
    if (finishing || finished) return
    setErr(''); stopTimer()
    if (finalAnswersOverride) {
      setAnswers(finalAnswersOverride)
      answersRef.current = finalAnswersOverride
    }
    let payload = finalAnswersOverride || answersRef.current

    if (!fromNext) {
      const rec = collect(true); if (!rec) return
      const merged = [...answersRef.current, rec]
      payload = merged
      setAnswers(merged)
      answersRef.current = merged
      await fetch(`/api/live${tokenQuery}`, { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ type:'answered', index: idx, ...rec }) })
    }

    try {
      setFinishing(true)
      const r = await fetch(`/api/submit${tokenQuery}`, {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ answers: payload, startedAt })
      })
      const d: SubmitResp = await r.json()
      if (!d.ok) throw new Error(d.error || 'Błąd zapisu')

      setFinished(true)
      if (reason) setTerminationReason(reason)
    } catch (e:any) {
      setErr(e.message || String(e))
    } finally {
      setFinishing(false)
    }
  }

  function beginExam() {
    if (!meta || started) return
    setStarted(true)
    setStartedAt(new Date().toISOString())
    strikesRef.current = 0
    setTerminationReason('')
    setErr('')
  }

  function terminateExam(reason: string) {
    if (finished || finishing || !started) return
    stopTimer()
    if (leaveTimerRef.current) { window.clearTimeout(leaveTimerRef.current); leaveTimerRef.current = null }
    const rec = collect(true)
    let finalAnswers = answersRef.current
    if (rec) {
      finalAnswers = [...answersRef.current, rec]
      setAnswers(finalAnswers)
      answersRef.current = finalAnswers
      fetch(`/api/live${tokenQuery}`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ type:'answered', index: idx, ...rec }) })
    }
    fetch(`/api/live${tokenQuery}`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ type:'terminated', index: idx, reason }) })
    setTerminationReason(reason)
    handleFinish(true, finalAnswers, reason)
  }

  if (!meta) return <Shell><p>{err || 'Ładowanie…'}</p></Shell>

  const showIntro = !started && !terminationReason && !finished

  const total = meta.questions.length
  const percent = Math.round((idx / Math.max(1, total)) * 100)
  const secondsLeft = Math.ceil(leftMs / 1000)
  const imgUrl = `/assets/${meta.exam}/${meta.questions[idx]?.id}.${meta.extension || 'jpg'}`

  if (terminationReason) return (
    <Shell>
      <div className="endCard danger">
        <h2>Egzamin przerwany</h2>
        <p>{terminationReason}</p>
        <button className="btn" onClick={() => window.location.href = '/'}>Wróć na stronę główną</button>
      </div>
      <style>{endCss}</style>
    </Shell>
  )

return finished ? (
  <Shell>
    <div className="endCard">
      <h2>Egzamin zakończony</h2>
      <p>Twoje odpowiedzi zostały przekazane do egzaminatora. Powodzenia!</p>
      {meta?.exam && <p className="muted">Egzamin: <b>{meta.exam}</b></p>}
      {meta?.examinerId && (
        <p className="muted">Szczegóły przekazane do egzaminatora (DCID: <code>{meta.examinerId}</code>).</p>
      )}
      <button className="btn" onClick={() => window.location.href = '/'}>Wróć na stronę główną</button>
    </div>
    <style>{endCss}</style>
  </Shell>
) : (
  <Shell>
    <div className={showIntro ? 'content blurred' : 'content'}>
      <div className="top">
        <div className="title">
          <h2>Egzamin {meta?.exam}</h2>
          <span className="badge">{(meta?.timeLimitSeconds ?? 15)}s / pytanie</span>
        </div>
        <div className="muted">{Math.min(idx + 1, total)} / {total}</div>
      </div>

      <div className="progress"><div className="bar" style={{ width: `${percent}%` }} /></div>

      <div className="card">
        <div className="cols">
          <div className="left">
            <div className="imageWrap">
              <img src={imgUrl} alt="Miejscówka" draggable={false} onContextMenu={e=>e.preventDefault()}/>
            </div>
            <div className="timer">
              <div className="timerTrack">
                <div className="timerFill" style={{ width: `${(leftMs / timeLimit) * 100}%` }} />
              </div>
              <div className="timerText">
                <span>Pozostały czas</span><span>{secondsLeft}s</span>
              </div>
            </div>
          </div>

          <div className="right">
            <label htmlFor="answer">Wpisz nazwę miejscówki:</label>
            <input id="answer" value={value} onChange={onChange} onPaste={onPaste} autoComplete="off" placeholder="np. Komenda LSPD" disabled={!started || finishing || finished}/>
            <div className="rowBtns">
              {idx < total - 1 && <button onClick={()=>handleNext(false)} disabled={!started || finishing}>Dalej ↵</button>}
              {idx === total - 1 && <button className="success" onClick={()=>handleFinish(false)} disabled={finishing || !started}>Zakończ ↵</button>}
            </div>
            {err && <p className="err">{err}</p>}
          </div>
        </div>
      </div>
    </div>

    {showIntro && (
      <div className="introOverlay">
        <div className="introCard">
          <h2>Gotowy na egzamin?</h2>
          <p className="muted">Kliknij poniżej, aby rozpocząć. Po starcie uruchomi się licznik czasu.</p>
          <button className="btn" onClick={beginExam}>Rozpocznij egzamin</button>
        </div>
      </div>
    )}
  </Shell>
)

}


function Shell({children}:{children:React.ReactNode}) {
  return (<div className="wrap"><style>{css}</style>{children}</div>)
}

const css = `
:root{--bg:#0b0d12;--card:#111525;--card2:#0e1220;--border:#1f2430;--text:#e8e8ec;--muted:#a6a8ad;--acc:#7a2cff;--ok:#2e9c4b;--err:#ff6b6b}
*{box-sizing:border-box}
body{background:linear-gradient(180deg,#0b0d12,#0a0c14);color:var(--text);font-family:Inter,system-ui,-apple-system,Segoe UI,Roboto,Arial;margin:0}
.wrap{max-width:1000px;margin:0 auto;padding:24px}
.content.blurred{filter:blur(6px);pointer-events:none;user-select:none;opacity:.7}
.top{display:flex;justify-content:space-between;align-items:center;margin-bottom:14px}
.title{display:flex;gap:10px;align-items:center}
.badge{padding:4px 10px;background:#1b1f2d;border:1px solid #2a3042;border-radius:999px;color:var(--muted);font-size:12px}
.muted{color:var(--muted)}
.progress{height:8px;background:#151a27;border:1px solid #20263a;border-radius:999px;overflow:hidden;margin:8px 0 18px}
.bar{height:100%;background:var(--acc);width:0%}
.card{background:radial-gradient(60% 120% at 0 0,rgba(122,44,255,.12),transparent),var(--card);border:1px solid var(--border);border-radius:20px;box-shadow:0 20px 60px rgba(0,0,0,.45);padding:20px}
.cols{display:grid;grid-template-columns:1fr 1fr;gap:20px}
.imageWrap{position:relative;min-height:360px;border-radius:14px;overflow:hidden;border:1px solid #262c3f;background:var(--card2)}
.imageWrap img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover}
input{width:100%;padding:14px;border-radius:12px;border:1px solid #2b3242;background:#0e1220;color:#fff;font-size:16px;outline:none}
input:focus{border-color:#3c4a6b;box-shadow:0 0 0 3px rgba(122,44,255,.2)}
input:disabled{opacity:.6;cursor:not-allowed}
.rowBtns{display:flex;gap:10px;margin-top:12px}
button{padding:12px 16px;border:0;border-radius:12px;background:var(--acc);color:#fff;font-weight:700;cursor:pointer}
button.success{background:var(--ok)}
.err{color:var(--err)}
.timer{margin-top:16px}
.timerTrack{height:8px;border-radius:999px;background:#1f2432;border:1px solid #2a3144;overflow:hidden}
.timerFill{height:100%;width:100%;background:linear-gradient(90deg,#24a0ff,#7a2cff)}
.timerText{display:flex;justify-content:space-between;margin-top:6px;color:#a6a8ad;font-size:14px}

.introOverlay{position:fixed;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(4,6,12,.55);backdrop-filter:blur(8px);z-index:10}
.introCard{background:var(--card);border:1px solid var(--border);border-radius:16px;padding:24px;box-shadow:0 20px 60px rgba(0,0,0,.55);max-width:420px;text-align:center}
.introCard .btn{margin-top:12px;width:100%}
`

const endCss = `
.endCard{
  max-width:720px;margin:60px auto;padding:24px;
  background:radial-gradient(60% 120% at 0 0,rgba(122,44,255,.12),transparent),var(--card);
  border:1px solid var(--border);border-radius:20px;box-shadow:0 20px 60px rgba(0,0,0,.45);
  text-align:center
}
.endCard.danger{border-color:#3a1f28;background:radial-gradient(60% 120% at 0 0,rgba(255,107,107,.14),transparent),var(--card)}
.endCard .muted{color:#a6a8ad}
.endCard code{background:#12182a;border:1px solid #203054;border-radius:6px;padding:2px 6px}
.btn{margin-top:12px;padding:12px 16px;border:0;border-radius:12px;background:#7a2cff;color:#fff;font-weight:700;cursor:pointer}
`
